// Pure, framework-agnostic logic for the checkbox-based subtotal range editor. Operates only on
// ordered lists of ids so it can be shared between ProjectTable.tsx/DrawLineItemsTable.tsx
// (ProjectLineItemDTO[], position tracked via `serialNumber`) and PayappTable.tsx Step 2
// (RowData[], position tracked via plain array order) — each caller adapts its own data shape
// into an ordered id list before calling these, then reconciles the result back.
//
// Model: a subtotal's "pool" is every id strictly between the previous subtotal's anchor
// (exclusive) and the next subtotal's anchor (exclusive) — the only ids it could ever claim,
// since a range can never cross into an adjacent subtotal's anchor itself. The "checked" set is
// always a contiguous block of pool ids ending at the anchor (this invariant is established by
// getDefaultCheckedIds and preserved by every toggleRangeItem call).

export interface RangeToggleResult {
  poolIds: string[];
  anchorId: string;
  checkedIds: Set<string>;
}

// The pool of ids a subtotal at `anchorId` can possibly claim: everything strictly between the
// previous subtotal's anchor (exclusive) and the next subtotal's anchor (exclusive).
export function computeRangePool(orderedIds: string[], previousAnchorId: string | null, nextAnchorId: string | null): string[] {
  const startIdx = previousAnchorId ? orderedIds.indexOf(previousAnchorId) + 1 : 0;
  const rawEndIdx = nextAnchorId ? orderedIds.indexOf(nextAnchorId) : orderedIds.length;
  const endIdx = rawEndIdx < 0 ? orderedIds.length : rawEndIdx;
  return orderedIds.slice(Math.max(0, startIdx), endIdx);
}

// Default checked set: the last `range` pool ids ending at (and including) the anchor, or the
// entire pool up to the anchor if range is null/undefined (the existing default behavior).
export function getDefaultCheckedIds(poolIds: string[], anchorId: string, range: number | null | undefined): Set<string> {
  const anchorIdx = poolIds.indexOf(anchorId);
  if (anchorIdx < 0) return new Set(anchorId ? [anchorId] : []);
  const start = range != null && range > 0 ? Math.max(0, anchorIdx - range + 1) : 0;
  return new Set(poolIds.slice(start, anchorIdx + 1));
}

// Applies one checkbox toggle. The pool is always laid out as three regions, each kept in the
// pool's original order (`originalPoolIds` — the pool as it was when the edit started), so that
// repeated checks/unchecks never scramble rows and re-checking a row restores its original slot:
//  - unchecked ids above the checked block — every unchecked id lands here ("moves to the top");
//  - the checked block, whose last id is the anchor (the subtotal row renders right after it);
//  - untouched unchecked ids below the anchor ("under the subtotal").
// Returns null for an invalid toggle (id not in the pool, or unchecking the only remaining item —
// a subtotal must always claim at least one line item).
export function toggleRangeItem(
  poolIds: string[],
  originalPoolIds: string[],
  anchorId: string,
  checkedIds: Set<string>,
  toggledId: string,
  checked: boolean,
): RangeToggleResult | null {
  if (checked === checkedIds.has(toggledId)) {
    return { poolIds, anchorId, checkedIds };
  }

  const anchorIdx = poolIds.indexOf(anchorId);
  if (anchorIdx < 0 || !poolIds.includes(toggledId)) return null;
  if (!checked && checkedIds.size <= 1) return null;

  const nextChecked = new Set(checkedIds);
  if (checked) nextChecked.add(toggledId);
  else nextChecked.delete(toggledId);

  const above: string[] = [];
  const block: string[] = [];
  const below: string[] = [];
  poolIds.forEach((id, idx) => {
    if (nextChecked.has(id)) block.push(id);
    else if (id === toggledId || idx < anchorIdx) above.push(id);
    else below.push(id);
  });

  const originalIndex = new Map(originalPoolIds.map((id, idx) => [id, idx]));
  const byOriginalOrder = (a: string, b: string) =>
    (originalIndex.get(a) ?? Number.MAX_SAFE_INTEGER) - (originalIndex.get(b) ?? Number.MAX_SAFE_INTEGER);
  above.sort(byOriginalOrder);
  block.sort(byOriginalOrder);
  below.sort(byOriginalOrder);

  return { poolIds: [...above, ...block, ...below], anchorId: block[block.length - 1], checkedIds: nextChecked };
}
